import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

/**
 * CanonTabsBar — la barra de pestañas del canon de UI/UX, píxel a píxel.
 *
 * Reproduce la barra que `MasterCrud` dibuja a partir de `formTabs`, tal y como la fija el
 * **ADR-027 §C**. Existe para que `MasterDocument` deje de tener DOS diseños propios de
 * pestañas —el subrayado de `HeaderRenderer` (14px, `border-b-2`) y los chips redondeados de
 * `LineTabsBar`— y ninguno de los dos se pareciese al de MasterCrud. Al vivir el estilo en UN
 * solo sitio, los dos consumidores no pueden volver a divergir en el siguiente PR
 * (ADR-027 §Apéndice C, «un solo dueño del estilo»).
 *
 * Lo que el canon fija y aquí se copia literal (§C.2, Regla A.2 — copiar, nunca aproximar):
 *
 * - botón `px-3 py-1.5 text-xs font-semibold`, `rounded-t-lg`, fondo `#f8f9fc`;
 * - activa `text-[#0c6ade]`; inactiva en `content-tertiary` con hover a `content-primary`;
 * - indicador deslizante: UN solo `<span aria-hidden>` de `2.5px` de grosor CONSTANTE
 *   (Regla C.1 — no se deriva de la pestaña), `rounded-[3px]`, `bg-[#0e79fd]`;
 * - los dos ejes del movimiento llevan curvas DISTINTAS a propósito: el rebote de la barra
 *   sale del `overshoot` de `left`. Va en estilo inline porque son dos transiciones con
 *   curvas distintas por propiedad, no un valor de la escala de Tailwind.
 *
 * Los colores son hex literales y no clases de paleta: la paleta de Tailwind v4 es `oklch`
 * y no reproduce estos hex —`bg-blue-600` no es `#0e79fd`— (Regla C.2).
 *
 * El indicador se coloca MIDIENDO la pestaña activa, así que se recalcula al cambiar de
 * pestaña, al redimensionar y cuando las etiquetas se resuelven: un catálogo i18n que llega
 * tarde cambia el ancho de todas.
 *
 * Accesibilidad: emite `role="tablist"` / `role="tab"` con `aria-selected`, `aria-controls`
 * y el `tabIndex` rotatorio del patrón ARIA Tabs, más las flechas ←/→ e Inicio/Fin. Eso lo
 * traía `LineTabsBar` y se conserva aquí — el canon de MasterCrud no lo tiene, y perderlo
 * al homologar habría sido cambiar apariencia a costa de comportamiento.
 *
 * @see ADR-027 §C — el canon literal
 */
export interface CanonTab {
  /** Identificador estable de la pestaña. Viaja a `data-tabkey` y a los `id` de ARIA. */
  key: string;
  /** Rótulo visible, ya traducido. */
  label: React.ReactNode;
  /** Icono opcional a la izquierda del rótulo. */
  icon?: React.ReactNode;
  /** `true` si la pestaña contiene algún campo obligatorio (§C.3: asterisco rojo). */
  hasRequired?: boolean;
  /** Nº de campos que siguen bloqueando el guardado (§C.3: badge rojo). `0` no pinta nada. */
  missingCount?: number;
}

export interface CanonTabsBarProps {
  tabs: CanonTab[];
  activeKey: string;
  onChange: (key: string) => void;
  /** Rótulo accesible de la barra. */
  ariaLabel?: string;
  /** Prefijo de los `id` de ARIA, para poder montar dos barras en una misma página. */
  idPrefix?: string;
  /**
   * `data-testid` de la barra. Por defecto `tabs`, que es el gancho del canon (§C.6).
   * Se puede cambiar porque `MasterDocument` monta DOS barras a la vez —la del encabezado y
   * la de líneas— y dos elementos con el mismo testid hacen que `getByTestId` lance por
   * ambigüedad en vez de encontrar el que se busca.
   */
  testId?: string;
  className?: string;
}

export function CanonTabsBar({
  tabs,
  activeKey,
  onChange,
  ariaLabel,
  idPrefix = 'tab',
  testId = 'tabs',
  className,
}: CanonTabsBarProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  // El indicador se posiciona midiendo la pestaña activa (§C.2). `data-tabkey` no es sólo un
  // gancho de test: es cómo la barra encuentra el botón que tiene que medir.
  const medirIndicador = useCallback(() => {
    const list = listRef.current;
    const btn = buttonRefs.current[activeKey];
    if (!list || !btn) return;
    const l = list.getBoundingClientRect();
    const b = btn.getBoundingClientRect();
    const left = Math.round(b.left - l.left + list.scrollLeft);
    const width = Math.round(b.width);
    // Sólo se re-renderiza si cambia: esto también cuelga de un ResizeObserver.
    setIndicator((prev) => (prev && prev.left === left && prev.width === width ? prev : { left, width }));
  }, [activeKey]);

  // `useLayoutEffect`: se mide y se aplica ANTES del primer paint, así el indicador no se ve
  // primero en el sitio equivocado y luego recolocado.
  useLayoutEffect(() => { medirIndicador(); }, [medirIndicador, tabs]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    window.addEventListener('resize', medirIndicador);
    // Las etiquetas pueden cambiar de ancho después del primer render (el catálogo i18n que
    // llega tarde), y eso no dispara `resize`.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medirIndicador) : null;
    if (ro) { ro.observe(list); Array.from(list.children).forEach((c) => ro.observe(c)); }
    return () => { window.removeEventListener('resize', medirIndicador); ro?.disconnect(); };
  }, [medirIndicador, tabs]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const keys = tabs.map((tab) => tab.key);
      const current = keys.indexOf(activeKey);
      if (current === -1) return;
      let next = current;
      switch (event.key) {
        case 'ArrowLeft': next = current === 0 ? keys.length - 1 : current - 1; break;
        case 'ArrowRight': next = current === keys.length - 1 ? 0 : current + 1; break;
        case 'Home': next = 0; break;
        case 'End': next = keys.length - 1; break;
        default: return;
      }
      event.preventDefault();
      const nextKey = keys[next];
      onChange(nextKey);
      // El foco sigue a la activación: es lo que espera el patrón ARIA Tabs cuando la
      // activación es automática.
      buttonRefs.current[nextKey]?.focus();
    },
    [tabs, activeKey, onChange],
  );

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
      className={clsx(
        'relative flex items-end gap-0.5 overflow-x-auto',
        'border-b border-border-secondary dark:border-dark-border-primary',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <button
            key={tab.key}
            ref={(node) => { buttonRefs.current[tab.key] = node; }}
            type="button"
            role="tab"
            id={`${idPrefix}-${tab.key}`}
            data-tabkey={tab.key}
            data-testid={`tab-${tab.key}`}
            aria-selected={isActive}
            aria-controls={`${idPrefix}panel-${tab.key}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={handleKeyDown}
            className={clsx(
              'tab-btn relative shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap',
              'cursor-pointer rounded-t-lg bg-[#f8f9fc] dark:bg-dark-bg-secondary transition-colors focus:outline-none',
              isActive
                ? 'text-[#0c6ade]'
                : 'text-content-tertiary dark:text-dark-content-tertiary hover:text-content-primary dark:hover:text-dark-content-primary hover:bg-[#eef0f3] dark:hover:bg-white/[0.06]',
            )}
          >
            {tab.icon && (
              <span className="shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5" aria-hidden="true">{tab.icon}</span>
            )}
            {tab.label}
            {tab.hasRequired && <span className="text-[#dc2626]">*</span>}
            {(tab.missingCount ?? 0) > 0 && (
              <span className="inline-flex items-center justify-center min-w-4 h-4 px-1 bg-[#dc2626] text-white rounded-full text-[9px] font-bold">
                {tab.missingCount}
              </span>
            )}
          </button>
        );
      })}

      {/* Indicador deslizante: UN solo span sobre la pestaña activa (§C.2). Grosor constante
          de 2.5px (Regla C.1). Los dos ejes llevan curvas distintas a propósito — el
          `overshoot` de `left` es lo que da el rebote de la barra. */}
      {indicator && (
        <span
          aria-hidden="true"
          className="absolute bottom-0 h-[2.5px] rounded-[3px] bg-[#0e79fd] pointer-events-none"
          style={{
            left: indicator.left,
            width: indicator.width,
            transition: 'left .5s cubic-bezier(.34,1.56,.64,1), width .42s cubic-bezier(.22,1,.36,1)',
          }}
        />
      )}
    </div>
  );
}
